<template>
  <div class="w-full h-full model-view" v-loading="loadingEngine">
    <img
      id="playerEngineId"
      class="engineVideo w-full"
      v-if="modelSetting.cloudRenderLoadMode === 'img'"
    />
    <video id="playerEngineId" autoplay playsinline muted class="engineVideo w-full" v-else></video>
    <!-- 模型进度条 -->
    <player-progress
      :loadType="loadType"
      :percentage="loadPercentage"
      v-if="showLoadProgress"
    ></player-progress>
  </div>
</template>

<script>
import { ModelSceneApi } from '@wiz-api/scene-management'
import { getPreferenceSettings } from './js/model/modelSetting'
import { loadSceneFromJson, isJSONObj, getNodesByNodeType } from './js/scene/scene'
import downloadFile from './js/model/modelDownload'

const modelSceneApi = new ModelSceneApi()

const PlayerProgress = () => {
  return import('./PlayerProgress')
}
export default {
  name: 'ModelView',
  components: { PlayerProgress },
  props: {
    loadModelType: {
      type: String,
      defalut: 'modelFileId',
    },
    sceneId: {
      type: String,
      defalut: '',
    },
    // 模型信息fileId、md5
    modelFileInfo: {
      type: Object,
      default: () => {},
    },
    modelTreeProp: {
      type: Object,
      default: () => {
        return {
          currentNodeItem: 0, // 当前激活的node
          currentNodeName: '', // 点选时当前激活的节点名称
          treeData: [], // 模型树数据
          defaultCheckedKey: [], // 模型树选中的节点
          filterItem: [], // 模型树不需要显示的节点及他的父节点列表
          modelHideList: [], // 模型隐藏队列
          modelTreeLoadEnd: false, // 模型树数据加载完毕
          spaceSwapStatus: false, // 交换空间状态
        }
      },
    },
    cloudRender: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      player: null, // player实例
      loadingEngine: false, // 引擎加载状态
      showLoadProgress: false, // 进度条显隐
      loadType: '', // 进度条类型
      loadPercentage: 0, // 进度条值
      sceneOptions: {
        isEnableGis: '',
        longitude: '',
        latitude: '',
        name: '',
        id: '',
        jsonData: '',
      },
      sceneRootNode: 0,
      sceneFileList: [],
      sceneFileCount: 0,
      sceneData: {},
      loadedModels: [],
      modelEngineId: '',
    }
  },
  computed: {
    // 模型相关设置
    modelSetting() {
      return _.get(Wiz, 'globalSettings.globalConfig')?.modelSetting || {}
    },
    // 云渲染协议
    cloudProtocol() {
      return this.modelSetting.cloudProtocol || 'ws'
    },
    // 云渲染服务路径
    cloudServerPath() {
      return this.modelSetting.cloudServerPathVulkan || 'renderapi.wiz.top:7082'
    },
    // wiz-iot-management配置
    wizIotConfig() {
      return Wiz.globalSettings.dynamicModules?.modules['wiz-iot-management']?.config || {}
    },
    folderName() {
      return this.wizIotConfig.folderName || 'SAAS'
    },
  },
  watch: {
    async sceneId(val, oldVal) {
      if (val !== oldVal && this.loadModelType === 'sceneId' && this.player) {
        const rootNode = await this.player.Native.Node.getRoot()
        const childrenNode = await this.player.Native.Node.getProperty(rootNode, ['children'])
        await this.player.Native.Node.destroy(
          childrenNode.children.map((item) => {
            return item.node
          })
        )
        this.getSceneInfo(val)
      }
      if (val === oldVal) {
        this.$emit('loadModelEnd')
      }
    },
    async sceneFileCount(num) {
      if (num === 0) {
        return
      }
      if (num >= this.sceneFileList.length) {
        await loadSceneFromJson(
          this.sceneData,
          this.sceneRootNode,
          this.player,
          this.sceneOptions,
          []
        )
        this.sceneFileCount = 0
        if (this.sceneData.cameraParams && isJSONObj(this.sceneData.cameraParams)) {
          const { eye, target, up } = JSON.parse(this.sceneData.cameraParams)
          await this.player.Native.Camera.moveTo(eye, target, up, 2)
        } else if (this.sceneOptions.isEnableGis) {
          await this.moveToLLH(this.sceneOptions.longitude, this.sceneOptions.latitude)
        }
        this.showLoadProgress = false
        this.loadedModels = []
        this.$emit('loadModelEnd')
      } else {
        this.checkTextureFile(this.sceneFileList[num])
      }
    },
    async modelFileInfo(val, oldVal) {
      if (val.FileId !== oldVal.FileId && this.loadModelType === 'modelFileId' && this.player) {
        await this.cloudRender.Node.destroyNode(oldVal.FileId)
        this.loadModels()
      } else if (oldVal.FileId) {
        this.$emit('loadModelEnd')
      }
    },
  },
  mounted() {
    console.log('mounted====')
    this.loadingEngine = true
    this.runPlayer()
  },
  methods: {
    generateGUID() {
      let d = new Date().getTime()
      if (window.performance && typeof window.performance.now === 'function') {
        d += performance.now()
      }
      const guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (d + Math.random() * 16) % 16 | 0
        d = Math.floor(d / 16)
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })
      return guid
    },
    // 监听模型加载进度
    checkModelLoad(data, node, name) {
      const modelList = _.filter(this.sceneFileList, (item) => {
        return (
          item.code &&
          item.code?.type === 'modelFile' &&
          _.last(item.code?.filePath.split('.')) === 'pr'
        )
      })
      if (!this.showLoadProgress) {
        this.loadType = '加载中...'
        this.showLoadProgress = true
      }
      if (data >= 1) {
        this.loadedModels.push(name)
      }
      this.loadPercentage = _.round((_.uniq(this.loadedModels).length / modelList.length) * 100, 2)
    },
    // 加载player引擎
    async runPlayer(engineId) {
      this.modelEngineId = this.generateGUID()
      const param = {
        signalServer: `${this.cloudProtocol}://${this.cloudServerPath}`,
        options: {
          engineId: engineId || this.modelEngineId,
          videoDom: document.getElementById('playerEngineId'), // 绑定视频标签
          userTimeout: 1000 * 60 * 60, // 用户操作超时设置，毫秒
          exitTimeout: 1000 * 60 * 1, // 用户超时后多久断开会话
          maxTimeout: 1000, // 会话超时时间，允许掉线后引擎保存多久，在该时间内可重新连接到会话，否则会创建新的会话
          // openDebug: true,
        },
      }
      const { data } = await this.cloudRender.Engine.loadEngine(param)
      if (data) {
        this.player = data.getDataInfo()
        this.cloudRender.Dispatch.on('Engine', async (e) => {
          if (e.type === 'UserTimeOut') {
            // 用户操作超时
            console.error('用户操作超时')
          }
          if (e.type === 'TimeOutExit') {
            // 断开会话
            console.error('超时断开')
          }
        })
        // 实例化完成将实例化后的player传出去
        this.$emit('sendPlayer', this.player)
        this.initPlayer(engineId)
      }
    },
    // 初始化player
    async initPlayer(engineId) {
      // 设置点选时其他节点半透明
      this.player.Native.Setting.set('color.pickup.bkAlpha', 'float', '0.5')
      this.player.Native.Tools.EventBrowseLClick.connect((e) => {
        console.log(e, '3')
        this.$emit('modelClick', e)
      })
      this.loadingEngine = false
      const settingsFileUrl = `data://../apps/${this.modelSetting.folderName}/settings.json`
      const settingsData = await this.player.Native.File.loadText(settingsFileUrl)
      if (settingsData) {
        const defaultSettings = JSON.parse(settingsData)
        _.forEach(defaultSettings.Show, (item) => {
          this.cloudRender.Setting.set(item.name, item.type, item.value)
        })
      }
      await getPreferenceSettings(true, this.player)
      if (this.loadModelType === 'modelFileId') {
        this.loadModels()
      } else if (this.loadModelType === 'sceneId') {
        this.getSceneInfo(this.sceneId)
      }
      // // 点选监听
      this.player.Native.Node.EventPickUpNode.connect(this.watchMsg)
    },
    // 获取场景信息
    async getSceneInfo(id) {
      if (id) {
        const [err, res] = await modelSceneApi.get(id)
        if (!err) {
          this.sceneOptions = _.pick(res, ['isEnableGis', 'longitude', 'latitude', 'name', 'id'])
        }
        const [jsonErr, jsonRes] = await modelSceneApi.getModelSecenRelatedJson({ id })
        if (!jsonErr) {
          this.sceneOptions.jsonData = jsonRes
        }
        // 获取到经纬度添加地图
        if (res.isEnableGis) {
          // this.player.Native.GisPlatform.setCameraTerrainCollisionEnabled(false)
          await this.cloudRender.GIS.loadGis(this.modelSetting.globalMapPath)
          this.moveToLLH(this.sceneOptions.longitude, this.sceneOptions.latitude)
        } else {
          await this.cloudRender.GIS.unLoadGis()
        }
        this.$emit('setSceneOptions', this.sceneOptions)
      }
      this.loadScene()
    },
    // 移动相机到经纬度
    async moveToLLH(long, lat) {
      if (long && lat) {
        const elevation = (await this.player.Native.GisPlatform.queryDEM([[long, lat, 0]]))[0]
        this.player.Native.Camera.addMoveToLLH([long, lat, 20000], 0)
        this.player.Native.Camera.addMoveToLLH([long, lat, elevation + 800], 8)
      }
    },
    // 加载场景
    async loadScene() {
      this.sceneRootNode = await this.player.Native.Node.getRoot()
      if (this.sceneOptions.jsonData && isJSONObj(this.sceneOptions.jsonData)) {
        this.sceneData = JSON.parse(this.sceneOptions.jsonData)
        this.sceneFileList = await getNodesByNodeType(this.sceneData, [])
        if (!_.isEmpty(this.sceneFileList)) {
          this.checkTextureFile(this.sceneFileList[0])
        } else {
          await loadSceneFromJson(
            this.sceneData,
            this.sceneRootNode,
            this.player,
            this.sceneOptions,
            []
          )
          if (this.sceneData.cameraParams && isJSONObj(this.sceneData.cameraParams)) {
            const { eye, target, up } = JSON.parse(this.sceneData.cameraParams)
            await this.player.Native.Camera.moveTo(eye, target, up, 2)
          } else if (this.sceneOptions.isEnableGis) {
            await this.moveToLLH(this.sceneOptions.longitude, this.sceneOptions.latitude)
          }
          this.$emit('loadModelEnd')
        }
      } else {
        console.log('场景数据为空')
        // const nodePtr = await this.player.Native.NodeProxy.createNode(
        //   this.sceneRootNode,
        //   this.sceneOptions.name
        // )
        // if (
        //   this.sceneOptions.isEnableGis &&
        //   this.sceneOptions.longitude &&
        //   this.sceneOptions.latitude
        // ) {
        //   const elevation = (
        //     await this.player.Native.GisPlatform.queryDEM([
        //       [this.sceneOptions.longitude, this.sceneOptions.latitude, 0],
        //     ])
        //   )[0]
        //   await this.player.Native.NodeProxy.setNodeGisPos(nodePtr, [
        //     this.sceneOptions.longitude,
        //     this.sceneOptions.latitude,
        //     elevation,
        //   ])
        // }
        // await this.player.Native.NodeProxy.setNodeInfo(nodePtr, {
        //   code: JSON.stringify({
        //     type: 'Root',
        //     fileType: '',
        //     filePath: '',
        //     scene: this.sceneOptions.id,
        //     isEnableGis: this.sceneOptions.isEnableGis,
        //   }),
        // })
        this.$emit('loadModelEnd')
      }
    },
    // 检查文件是否存在
    async checkTextureFile(data) {
      if (data && data.code && data.code.modelFileId) {
        if (data.code.materialFileId && data.code.materialFileId !== 'undefined') {
          const url = `data://../apps/${data.code.filePath.split('/')[4]}/models/${
            data.code.modelFileId
          }/${data.code.modelFileId}.zip`
          const fileInfo = {
            id: data.code.materialFileId,
            url,
            fileType: 'zip',
          }
          const apiConfig = {
            path: `${
              Wiz.GetApiClientInstance('file-management')?.basePath
            }/api/file-management/files/${data.code.materialFileId}/download`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
            },
          }
          const res = await downloadFile(
            this.player,
            fileInfo,
            apiConfig,
            this.progressMsg,
            this.downloadMaterialMsg(data)
          )
          if (res.type === 0 && res.success) {
            this.checkModelFile(data)
          } else {
            this.showLoadProgress = true
            this.loadType = '下载中...'
          }
        } else {
          this.checkModelFile(data)
        }
      }
    },
    // 检查模型是否存在
    async checkModelFile(data) {
      const fileType = _.last(data.code.filePath.split('.')) || 'pr'
      const url = data.code.filePath
      const fileInfo = {
        id: data.code.modelFileId,
        url,
        fileType,
      }
      const apiConfig = {
        path: `${Wiz.GetApiClientInstance('file-management')?.basePath}/api/file-management/files/${
          data.code.modelFileId
        }/download`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
        },
      }
      const res = await downloadFile(
        this.player,
        fileInfo,
        apiConfig,
        this.progressMsg,
        this.downloadMsg
      )
      if (res.type === 0 && res.success) {
        // 有模型就加载模型
        this.sceneFileCount += 1
      } else {
        this.showLoadProgress = true
        this.loadType = '下载中...'
      }
    },
    // 监听下载模型是否成功
    downloadMsg(file, err) {
      this.loadPercentage = 0
      this.showLoadProgress = false
      if (err) {
        this.$message.warning(`下载文件失败，${file}, ${err}`)
      }
      this.sceneFileCount += 1
    },

    // 监听下载模型是否成功
    downloadMaterialMsg(data) {
      const that = this
      const fun = async (file, err) => {
        that.showLoadProgress = false
        that.loadPercentage = 0
        if (err) {
          that.$message.warning(`下载贴图文件失败，${file}, ${err}`)
        }
        setTimeout(() => {
          that.checkModelFile(data)
        }, 1500)
      }
      return fun
    },

    // 监听下载进度
    progressMsg(file, bytesReceived, bytesTotal) {
      if (bytesTotal) {
        this.loadPercentage = (bytesReceived / bytesTotal) * 100
      }
    },
    // FileId加载模型
    async loadModels() {
      let modelFileList = []
      if (this.loadModelType === 'modelFileId') {
        modelFileList = this.modelFileInfo.FileId ? [this.modelFileInfo] : []
      }
      const modelList = _.map(modelFileList, (item) => {
        return {
          targetUrl: `data://../apps/${this.folderName}/models/${item.Md5}/${item.Md5}.pr`,
          fileUrl: `${this.wizIotConfig.modelFileServerUrl}/${item.Md5}`,
          md5: item.Md5,
          tempPath: `data://../temps/models/${item.Md5}/${item.Md5}.pr`,
          options: {
            nodeId: item.FileId,
            httpOptions: {
              methods: 'GET',
              header: { Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}` },
            },
            // 贴图文件
            // modelDecals: {
            //   targetUrl: 'data://models/sampler.pr',
            //   fileUrl: '',
            //   tempPath: '',
            //   md5: '',
            // }
          },
        }
      })
      this.cloudRender.Node.addNode(modelList)
      this.cloudRender.Dispatch.on('loadNode', this.loadModelConnect)
    },
    // FileId监听模型加载进度
    async loadModelConnect(res) {
      if (res.status && res.data && res.data.type === 'nodeStatus') {
        switch (res.data.currentState) {
          case 1:
            this.showLoadProgress = true
            this.loadType = '加载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 3:
            this.showLoadProgress = true
            this.loadType = '下载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 4:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            if (this.loadModelType === 'modelFileId') {
              const nodeInfo = await this.cloudRender.Node.getInfo(this.modelFileInfo.FileId)
              await this.player.Native.Camera.moveToNode(nodeInfo?.data.nodeInfo.node, 2)
              this.$emit('loadModelEnd')
            }
            break
          case 5:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          default:
            break
        }
      } else if (!res.status) {
        this.$message.warning(res.message)
        this.showLoadProgress = false
        this.loadType = ''
        this.loadPercentage = 0
      }
    },
    // 模型点选
    watchMsg(data) {
      console.log(data, '2')
      if (data.node) {
        const nodeInfo = this.player.Native.Node.getProperty(data.node, ['name'])
        // 获取选中模型节点的name
        this.modelTreeProp.currentNodeName = nodeInfo ? nodeInfo.name : ''
      }
    },
  },
  beforeDestroy() {
    console.log('destroyEngine')
    this.cloudRender.Engine.destroyEngine()
  },
  deactivated() {
    console.log('deactivatedEngine')
    this.cloudRender.Engine.destroyEngine()
  },
}
</script>

<style lang="scss" scoped>
.engineVideo {
  height: 100%;
  object-fit: fill;
}
::v-deep .el-loading-mask {
  background-color: transparent !important;
}
.model-view {
  transform: scale(1, 1);
  transform-origin: 'top left';
}
</style>
